import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import { getProjectSlug } from '../utils/slugify';
import ScreenshotCarousel from '../components/ScreenshotCarousel';
import SupportSection from '../components/SupportSection';
import Loading from './Loading';
import FadeIn from '../components/FadeIn';

const PROJECT_IDS = {
  SHOT_LISTS: 'DREAuENBsq0mtY9tHYyr',
  CAST_AND_CREW: 'LzucjEOJFr8A1vzMfxza',
  BREAKDOWNS: 'Val6dCh3zc0NVxrYOuMM',
};

export default function ProjectDetail() {
  const { id: slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasFAQs, setHasFAQs] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const featuresObserver = useRef(null);
  const featuresRef = (node) => {
    if (!node) return;
    if (featuresObserver.current) featuresObserver.current.disconnect();
    featuresObserver.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setFeaturesVisible(true);
          featuresObserver.current.disconnect();
        }
      },
      { threshold: 0 }
    );
    featuresObserver.current.observe(node);
  };

  useEffect(() => {
    let isMounted = true;

    async function fetchProject() {
      try {
        // First, fetch all projects to find the one matching the slug
        const projectsSnap = await getDocs(collection(db, 'development'));

        if (!isMounted) return;

        // Find project by slug
        const matchingProject = projectsSnap.docs.find(doc => {
          const projectData = doc.data();
          return getProjectSlug({ name: projectData.name }) === slug;
        });

        if (matchingProject) {
          const projectId = matchingProject.id;
          const projectData = { id: projectId, ...matchingProject.data() };
          setProject(projectData);

          // Check if FAQs exist
          const faqsRef = collection(db, 'development', projectId, 'faqs');
          const faqsSnap = await getDocs(faqsRef);
          setHasFAQs(!faqsSnap.empty);

          // Fetch screenshots ordered by order field
          const screenshotsSnap = await getDocs(
            query(collection(db, 'development', projectId, 'screenshots'), orderBy('order'))
          );
          setScreenshots(screenshotsSnap.docs.map(d => d.data().url));
        } else {
          setProject(null);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching project:', error);
        if (isMounted) {
          setProject(null);
          setLoading(false);
        }
      }
    }

    fetchProject();

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
        padding: '48px 16px',
      }}>
        <Loading />
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{
        maxWidth: MAX_CONTENT_WIDTH,
        margin: '0 auto',
        padding: '48px 16px',
        textAlign: 'center',
      }}>
        <p style={{ color: 'var(--color-text-secondary)' }}>Project not found.</p>
      </div>
    );
  }

  return (
    <FadeIn>
      {/* Header/Banner Card */}
      <div
        style={{
          position: 'relative',
          backgroundColor: '#000000',
          overflow: 'hidden',
        }}
      >
        {/* Film grain */}
        {!project.bannerBackgroundURL && (
          <>
            <svg style={{ position: 'absolute', width: 0, height: 0 }}>
              <filter id="project-grain">
                <feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" />
                <feColorMatrix type="saturate" values="0" />
              </filter>
            </svg>
            <div style={{
              position: 'absolute',
              inset: '-50%',
              filter: 'url(#project-grain)',
              pointerEvents: 'none',
              opacity: 0.14,
            }} />
          </>
        )}
        {/* Background Image */}
        {project.bannerBackgroundURL && (
          <>
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url(${project.bannerBackgroundURL})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
              }}
            />
          </>
        )}
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 'calc(100vh - 200px)',
          padding: '48px 16px',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
          }}>
          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: 900,
            fontFamily: 'Helvetica, Arial, sans-serif',
            letterSpacing: '-0.05em',
            margin: 0,
            color: '#ffffff',
            lineHeight: 1.1,
            textAlign: 'center',
            textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)',
          }}>
            {project.name || 'Untitled Project'}
          </h1>
          {project.tagline && (
            <p style={{
              fontSize: '1.125rem',
              fontWeight: 400,
              fontFamily: 'Helvetica, Arial, sans-serif',
              margin: 0,
              color: '#cccccc',
              textAlign: 'center',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
            }}>
              {project.tagline}
            </p>
          )}
          {(project.externalLinks?.appStore || project.externalLinks?.playStore) && (
            <div style={{
              marginTop: '16px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
            }}>
              {project.externalLinks?.appStore && (
                <a
                  href={project.externalLinks.appStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    display: 'inline-block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <img
                    src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us"
                    alt="Download on the App Store"
                    style={{
                      height: '60px',
                      width: 'auto',
                    }}
                  />
                </a>
              )}
              {project.externalLinks?.playStore && (
                <a
                  href={project.externalLinks.playStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    transition: 'opacity 0.2s ease, transform 0.2s ease',
                    display: 'inline-block',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '0.8';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <img
                    src="/images/google-play-badge.png"
                    alt="Get it on Google Play"
                    style={{
                      height: '68px',
                      width: 'auto',
                    }}
                  />
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {/* Screenshot carousel */}
      {screenshots.length > 0 && <div style={{ borderTop: '1px solid #1a1a1a' }}><ScreenshotCarousel screenshots={screenshots} /></div>}

      {/* Cast & Crew Showcase */}
      {project.id === PROJECT_IDS.CAST_AND_CREW && (
        <div style={{
          width: '100%',
          minHeight: 'calc(100vh - 200px)',
          backgroundColor: '#000000',
          borderTop: '1px solid #1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 16px',
        }}>
          <div style={{
            maxWidth: '1200px',
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '64px',
            flexWrap: 'wrap',
          }}>
            {/* Text Content */}
            <div
              className="showcase-text"
              style={{
                flex: '1 1 400px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <h2 style={{
                fontSize: 'clamp(2.75rem, 4vw, 3rem)',
                fontWeight: 800,
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: '#ffffff',
                margin: 0,
                letterSpacing: '-0.05em',
                lineHeight: 1.2,
              }}>
                Your entire crew, one tap away.
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                color: '#cccccc',
                margin: 0,
                lineHeight: 1.6,
              }}>
                Manage contacts, departments, and availability on set.
              </p>
            </div>

            {/* Image with glow behind */}
            <div style={{
              flex: '1.5 1 500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(ellipse at center, rgba(40, 50, 70, 0.5) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <img
                src="/images/breakdowns-showcase.png"
                alt="Cast & Crew app showing contact details and department management"
                style={{
                  width: '100%',
                  maxWidth: '900px',
                  height: 'auto',
                  objectFit: 'contain',
                  position: 'relative',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Breakdowns Showcase */}
      {project.id === PROJECT_IDS.BREAKDOWNS && (
        <div style={{
          width: '100%',
          minHeight: 'calc(100vh - 200px)',
          backgroundColor: '#000000',
          borderTop: '1px solid #1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 16px',
        }}>
          <div style={{
            maxWidth: '1200px',
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '64px',
            flexWrap: 'wrap',
          }}>
            {/* Text Content */}
            <div
              className="showcase-text"
              style={{
                flex: '1 1 400px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <h2 style={{
                fontSize: 'clamp(2.75rem, 4vw, 3rem)',
                fontWeight: 800,
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: '#ffffff',
                margin: 0,
                letterSpacing: '-0.05em',
                lineHeight: 1.2,
              }}>
                Every scene, broken down.
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                color: '#cccccc',
                margin: 0,
                lineHeight: 1.6,
              }}>
                Characters, wardrobe, props, and every asset organized by scene.
              </p>
            </div>

            {/* Image with glow behind */}
            <div style={{
              flex: '1.5 1 500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(ellipse at center, rgba(40, 50, 70, 0.5) 0%, transparent 70%)',
                pointerEvents: 'none',
              }} />
              <img
                src="/images/cast-and-crew-showcase.png"
                alt="Script Breakdowns app showing scenes, characters, wardrobe, and assets"
                style={{
                  width: '100%',
                  maxWidth: '900px',
                  height: 'auto',
                  objectFit: 'contain',
                  position: 'relative',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Multi-Device Section */}
      {project.id === PROJECT_IDS.SHOT_LISTS && (
        <div style={{
          width: '100%',
          minHeight: 'calc(100vh - 200px)',
          backgroundColor: '#0d0d0d',
          borderTop: '1px solid #1a1a1a',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 16px',
        }}>
          <div style={{
            maxWidth: '1200px',
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '64px',
            flexWrap: 'wrap',
          }}>
            {/* Text Content */}
            <div
              className="multi-device-text"
              style={{
                flex: '1 1 400px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <h2 style={{
                fontSize: 'clamp(2.75rem, 4vw, 3rem)',
                fontWeight: 800,
                fontFamily: 'Helvetica, Arial, sans-serif',
                color: '#ffffff',
                margin: 0,
                letterSpacing: '-0.05em',
                lineHeight: 1.2,
              }}>
                From the small screen to the big screen
              </h2>
              <p style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                color: '#cccccc',
                margin: 0,
                lineHeight: 1.6,
              }}>
                Collaborate on your shot lists across iPhone, iPad, and Mac.
              </p>
            </div>
            <style>
              {`
                .multi-device-text,
                .showcase-text {
                  text-align: center;
                }
                @media (min-width: 1024px) {
                  .multi-device-text,
                  .showcase-text {
                    text-align: left;
                  }
                }
              `}
            </style>

            {/* Image */}
            <div style={{
              flex: '1.5 1 500px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <img
                src="https://firebasestorage.googleapis.com/v0/b/davinese-dcf1a.firebasestorage.app/o/work-together.png?alt=media&token=ded7475d-71a5-4654-a330-3b26550d4e76"
                alt="Shot Lists on iPhone, iPad, and Mac"
                style={{
                  width: '100%',
                  maxWidth: '900px',
                  height: 'auto',
                  objectFit: 'contain',
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      {project.id === PROJECT_IDS.SHOT_LISTS && (
        <div
          ref={featuresRef}
          style={{
            width: '100%',
            minHeight: 'calc(100vh - 200px)',
            backgroundColor: '#0a0a0a',
            borderTop: '1px solid #1a1a1a',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '48px 16px 16px 16px',
            overflow: 'hidden',
          }}
        >
          <div></div>
          <div style={{
            maxWidth: '1200px',
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '32px 48px',
          }}>
            {[
              { text: 'Multiple Frames per Shot', important: true },
              { text: 'Any-Angle Viewfinder', important: true },
              { text: 'Offline Mode', important: false },
              { text: 'Props for PD', important: true },
              { text: 'Camera Movement', important: true },
              { text: 'Sort by Shot Sizes', important: false },
              { text: 'Filter Shots by Characters', important: false },
              { text: 'Photo AutoFill', important: true },
              { text: 'Tilt vs. Roll', important: false },
              { text: 'Private On-Device AI', important: false },
              { text: 'Viewfind—Later', important: true },
            ].map((feature, index) => (
              <div
                key={index}
                style={{
                  fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                  fontWeight: 700,
                  fontFamily: 'Helvetica, Arial, sans-serif',
                  color: feature.important ? '#ffffff' : '#555555',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  opacity: featuresVisible ? 1 : 0,
                  transform: featuresVisible ? 'translateY(0)' : 'translateY(24px)',
                  transition: `opacity 0.7s ease ${index * 0.15}s, transform 0.7s ease ${index * 0.15}s`,
                }}
              >
                {feature.text}
              </div>
            ))}
          </div>
          <p style={{
            fontSize: '0.875rem',
            color: '#888888',
            margin: 0,
            lineHeight: 1.5,
            textAlign: 'center',
            opacity: featuresVisible ? 1 : 0,
            transition: `opacity 0.7s ease ${11 * 0.15}s`,
          }}>
            Private On-Device AI uses Apple Intelligence and requires iOS/iPadOS 26, macOS 26, or visionOS 26 or higher.
          </p>
        </div>
      )}

      {/* Support & Legal Links Section */}
      {[PROJECT_IDS.SHOT_LISTS, PROJECT_IDS.CAST_AND_CREW, PROJECT_IDS.BREAKDOWNS].includes(project.id) && (
        <SupportSection
          slug={slug}
          hasFAQs={hasFAQs}
        />
      )}

    </FadeIn>
  );
}
